import type { JSX } from 'react';
import { cn } from '@/lib/utils';
import { useLanguageStore } from '@/stores/language.store';

/**
 * F5-T3 · El toggle EN/ES del chrome, extraído del NavigatorShell al chasis:
 * el mockup «Navigator 01» lo declara parte del chrome que «el uploader y el
 * admin heredan con otro contenido», y Admin 01 dibuja EL MISMO toggle en su
 * barra superior — dos copias serían el duplicado que T2 existe para matar
 * (precedente E-20; acta E-23).
 *
 * D-07 (CEO, 2026-08-21) el toggle vuelve · Canvas-3 (CEO, 2026-08-23) la
 * forma final: pastilla navy radio 999, **thumb gold de 42px que se desliza**
 * (translateX 0↔44px, 250ms, la misma curva del subrayado), siglas EN/ES en
 * Montserrat 800 12px — activa en navy sobre el gold, inactiva #9fb6c8 que
 * pasa a blanco al hover. Una sola variable de idioma (interfaz + guion),
 * decisión del CEO del 08-21; el nombre completo vive en `aria-label`.
 * El gold es el reservado #ffce03 (`nav-gold`, handoff F5 §1) desde F5-T0
 * 2026-08-27 — antes usaba nav-amber #d9a540 por falta del token (desviación
 * 1 del acta E-03, cerrada).
 */
export function LangToggle(): JSX.Element {
  const lang = useLanguageStore((s) => s.lang);
  const setLang = useLanguageStore((s) => s.setLang);
  const langs: Array<{ code: 'en' | 'es'; name: string }> = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Espanol' },
  ];
  const idx = langs.findIndex((l) => l.code === lang);
  return (
    <div
      role="group"
      aria-label="Language"
      className="relative inline-flex items-center gap-[2px] flex-none bg-nav-navy rounded-full p-[3px] ml-[2px]"
      data-chassis="lang-toggle"
    >
      <span
        aria-hidden
        className="absolute top-[3px] left-[3px] w-[42px] h-[calc(100%-6px)] rounded-full bg-nav-gold transition-transform duration-[250ms] ease-[cubic-bezier(.4,0,.2,1)]"
        style={{ transform: `translateX(${Math.max(0, idx) * 44}px)` }}
      />
      {langs.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          aria-pressed={lang === l.code}
          aria-label={l.name}
          lang={l.code}
          className={cn(
            'relative z-[1] w-[42px] text-center font-head font-extrabold text-xs tracking-[.04em] py-[7px] rounded-full border-0 bg-transparent cursor-pointer transition-colors active:scale-95',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
            lang === l.code ? 'text-nav-navy' : 'text-[#9fb6c8] hover:text-white',
          )}
        >
          {l.code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
